import React from 'react';
import { Col, DatePicker, Input, InputNumber, Row, Select, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import BulkBatchPicker from '@/components/BulkBatchPicker';

const { Text } = Typography;
const Label = ({ children, hint }) => (
  <label className="font-medium mb-1 block">{children}{hint && <Tooltip title={hint}><span className="ml-1 text-gray-400 text-xs">ⓘ</span></Tooltip>}</label>
);

export const ORDER_FIELDS = ['po_number', 'so_number', 'customer', 'market', 'campaign_no', 'order_quantity', 'order_uom', 'planned_start', 'planned_end', 'target_release_date', 'priority', 'packaging_order_ref', 'change_control_ref', 'notes'];

/** BP3: the bulk → packaging link riding next to `order_info` in the issuance payload (packaging recipes only). */
export const PACKAGING_FIELDS = ['bulk_batch_number', 'bulk_units', 'at_risk_reason', 'change_control_ref'];
export const packagingPayload = (values) => {
  const link = values.packaging_link;
  if (!link || !link.bulk_batch_number) return {};
  const o = { bulk_batch_number: link.bulk_batch_number };
  if (link.bulk_units) o.bulk_units = link.bulk_units;
  if (link.at_risk) {
    o.at_risk_reason = link.at_risk_reason || '';
    o.change_control_ref = link.change_control_ref || values.order_change_control_ref || '';
  }
  return o;
};

export const orderPayload = (values) => {
  const o = {};
  ORDER_FIELDS.forEach((k) => {
    const v = values[`order_${k}`];
    if (v === undefined || v === null || v === '') return;
    o[k] = dayjs.isDayjs(v) ? (k === 'target_release_date' ? v.format('YYYY-MM-DD') : v.toISOString()) : v;
  });
  return Object.keys(o).length ? o : null;
};

/**
 * Release-BMR order metadata (what the batch is being made for). Rides in the issuance payload as
 * `order_info` and is stored on BatchOrderInfo; frozen once the batch starts.
 */
export default function OrderInfoFields({ values, setFieldValue, markets = [], compact = false, dense = false, shelfLifeMonths = null, isPackaging = false, recipeId = null, onPackagingLink = null }) {
  const set = (k) => (v) => setFieldValue(`order_${k}`, v);
  const val = (k) => values[`order_${k}`];
  // Provisional expiry: (planned start or today) + shelf life, last day of that month — the same rule
  // `batch_order_view.provisional_expiry` applies server-side, so the form and the issued batch agree.
  const expiryText = shelfLifeMonths
    ? (val('planned_start') ? dayjs(val('planned_start')) : dayjs()).add(shelfLifeMonths, 'month').endOf('month').format('DD MMM YYYY')
    : '—';
  // `dense`: the full-width Issue Batch page — four per row and tighter gaps so the
  // whole form fits one screen.
  const span = dense ? 6 : compact ? 12 : 8;
  const gap = <div className={dense ? 'h-3' : 'h-4'} />;
  return (
    <Row gutter={[16, 0]}>
      {isPackaging && (
        <Col span={24}>
          <BulkBatchPicker recipeId={recipeId} market={val('market')} value={values.packaging_link?.bulk_batch_number || values.source_master_batch || null} compact={compact}
            onChange={(link) => {
              setFieldValue('packaging_link', link);
              if (link?.change_control_ref && !val('change_control_ref')) setFieldValue('order_change_control_ref', link.change_control_ref);
              if (link?.theoretical_packs != null) { set('order_quantity')(link.theoretical_packs); set('order_uom')('Packs'); }
              if (onPackagingLink) onPackagingLink(link);
            }} />
        </Col>
      )}
      <Col xs={24} lg={span}><Label hint="Customer purchase order this batch fulfils">PO number</Label>
        <Input value={val('po_number') || ''} onChange={(e) => set('po_number')(e.target.value)} placeholder="PO-2026-04711" />{gap}</Col>
      <Col xs={24} lg={span}><Label hint="Sales / production order in the ERP">SO / production order</Label>
        <Input value={val('so_number') || ''} onChange={(e) => set('so_number')(e.target.value)} placeholder="SO-118234" />{gap}</Col>
      <Col xs={24} lg={span}><Label>Customer</Label>
        <Input value={val('customer') || ''} onChange={(e) => set('customer')(e.target.value)} placeholder="Customer / distributor" />{gap}</Col>
      <Col xs={24} lg={span}><Label hint="Drives the batch-number mask, label language and regulations">Market</Label>
        <Select style={{ width: '100%' }} allowClear showSearch value={val('market') || undefined} onChange={set('market')}
          // Only the server's markets (the record's product markets): a name outside the market master
          // would be refused at issue (CPV-01 review), so nothing is offered that the server does not know.
          options={markets.map((m) => ({ value: m, label: m }))} disabled={!markets.length}
          placeholder={markets.length ? 'Market' : 'No markets on this record'} />{gap}</Col>
      <Col xs={24} lg={span}><Label hint="Same-product consecutive batches; cleaning rules count campaigns">Campaign no.</Label>
        <Input value={val('campaign_no') || ''} onChange={(e) => set('campaign_no')(e.target.value)} placeholder="CMP-26-014" />{gap}</Col>
      <Col xs={24} lg={span}><Label>Order quantity</Label>
        <Input.Group compact style={{ display: 'flex' }}>
          <InputNumber style={{ flex: 1 }} min={0} value={val('order_quantity')} onChange={set('order_quantity')} placeholder="14 000" />
          <Select style={{ width: 120 }} allowClear value={val('order_uom') || undefined} onChange={set('order_uom')} options={['Cartridge', 'Vial', 'Syringe', 'Ampoule', 'Units', 'kg', 'L'].map((u) => ({ value: u, label: u }))} placeholder="unit" />
        </Input.Group>{gap}</Col>
      <Col xs={24} lg={span}><Label>Planned start</Label>
        <DatePicker showTime style={{ width: '100%' }} value={val('planned_start') ? dayjs(val('planned_start')) : null} onChange={set('planned_start')} format="DD-MMM-YYYY HH:mm" />{gap}</Col>
      <Col xs={24} lg={span}><Label>Planned end</Label>
        <DatePicker showTime style={{ width: '100%' }} value={val('planned_end') ? dayjs(val('planned_end')) : null} onChange={set('planned_end')} format="DD-MMM-YYYY HH:mm" />{gap}</Col>
      <Col xs={24} lg={span}><Label hint="When QA release is needed for shipment">Target release date</Label>
        <DatePicker style={{ width: '100%' }} value={val('target_release_date') ? dayjs(val('target_release_date')) : null} onChange={set('target_release_date')} format="DD-MMM-YYYY" />{gap}</Col>
      <Col xs={24} lg={span}><Label>Priority</Label>
        <Select style={{ width: '100%' }} value={val('priority') || 'NORMAL'} onChange={set('priority')} options={['LOW', 'NORMAL', 'HIGH', 'URGENT'].map((p) => ({ value: p, label: p }))} />{gap}</Col>
      <Col xs={24} lg={span}><Label hint="The BPR / packaging batch this bulk feeds (genealogy)">Packaging order ref.</Label>
        <Input value={val('packaging_order_ref') || ''} onChange={(e) => set('packaging_order_ref')(e.target.value)} placeholder="BPR batch / MPR order" />{gap}</Col>
      <Col xs={24} lg={span}><Label hint="Required when the issue deviates from the approved MBMR (non-standard size, market)">Change-control ref.</Label>
        <Input value={val('change_control_ref') || ''} onChange={(e) => set('change_control_ref')(e.target.value)} placeholder="CC/0310/2026/0201" />{gap}</Col>
      <Col xs={24} lg={dense ? 18 : 24}><Label>Notes for the floor</Label>
        <Input.TextArea rows={dense ? 1 : 2} value={val('notes') || ''} onChange={(e) => set('notes')(e.target.value)} placeholder="Special instructions, customer requirements, sample requests…" /><div className="h-2" /></Col>
      <Col xs={24} md={dense ? 24 : 8} lg={dense ? 6 : 8}>
        <div style={{ marginBottom: 8 }}>
          {dense ? <Label>Expiry (provisional)</Label> : <Text type="secondary" style={{ fontSize: 12 }}>Expiry (provisional)</Text>}
          <Input readOnly value={expiryText} style={{ fontWeight: 600, marginTop: dense ? 0 : 4 }} />
        </div>
      </Col>
      <Col span={24}><Text type="secondary" style={{ fontSize: 12 }}>{shelfLifeMonths
        ? `Provisional expiry = ${val('planned_start') ? 'planned start' : 'today'} + ${shelfLifeMonths} months, last day of that month. It is replaced by the real expiry when the manufacturing-date rule fires during execution.`
        // Two different reasons for the "—" above, and they need different fixes:
        // nothing selected yet (pick an MBMR) vs a product with no shelf life
        // on its master record (go set it). `recipeId` is what tells them apart.
        : !recipeId
          ? 'Select an MBMR to compute the provisional expiry — it comes from the shelf life on the selected master record’s product.'
          : "The product has no shelf life on its master record, so no expiry can be computed — set it on the product to print an expiry on this batch."}</Text></Col>
      {!dense && <Col span={24}><Text type="secondary" style={{ fontSize: 12 }}>Stored on the batch, shown in the batch header and report, frozen once the batch starts.</Text></Col>}
    </Row>
  );
}
